import wNumb from 'wnumb'
import metricSuffix from 'metric-suffix'
import List from 'list.js'
import clickOutside from 'click-outside'
import { createEvent, debounce } from './_helpers'

const defaultOptions = {
  dropdownOpenClass: 'buy-dropdown--open',
  dropdownCollapsedClass: 'buy-dropdown--collapsed',
  listOptionsSelector: 'li',
  listMinType: 'min',
  listMaxType: 'max',
  listMinId: 'min-currency-list',
  listMaxId: 'max-currency-list',
  listOptions: {
    valueNames: [{ data: ['value', 'period'] }],
  },
}

class CurrencyList {
  constructor(container, options) {
    this.options = { ...defaultOptions, ...options }
    this.container = container
    this.minListInstance = new List(
      this.options.listMinId,
      this.options.listOptions
    )
    this.maxListInstance = new List(
      this.options.listMaxId,
      this.options.listOptions
    )
    this.containerWrapper = container.querySelector('[data-wrapper]')
    this.valueContainer = container.querySelector('[data-value-container]')
    this.periodRadioInputs = container.querySelectorAll('[data-period-input]')
    this.minInput = container.querySelector(
      `[data-value-input="${this.options.listMinType}"]`
    )
    this.maxInput = container.querySelector(
      `[data-value-input="${this.options.listMaxType}"]`
    )
    this.dropdownLists = container.querySelectorAll('[data-list]')
    this.selectedPeriod = this.getSelectedPeriod()
    this.prefix = container.dataset.prefix
    this.optionsFormat = wNumb({
      prefix: `${this.prefix} `,
      thousand: "'",
      decimals: 0,
    })
    this.valueContainerFormat = wNumb({
      edit: value => `${this.prefix}  ${metricSuffix(value, 3)}`,
    })

    this.init()
  }

  getSelectedPeriod() {
    return [...this.periodRadioInputs].find(period => period.checked).value
  }

  checkCollapsed() {
    this.container.classList.toggle(
      this.options.dropdownCollapsedClass,
      this.valueContainer.innerText.length !== 0
    )
  }

  updateValueContainer() {
    const minValue = this.minInput.value
    const maxValue = this.maxInput.value

    if (minValue.length && maxValue.length) {
      this.valueContainer.innerHTML = `${this.valueContainerFormat.to(
        +minValue
      )} - ${this.valueContainerFormat.to(+maxValue)}`
    } else if (!minValue.length && maxValue.length) {
      this.valueContainer.innerHTML = `< ${this.valueContainerFormat.to(
        +maxValue
      )}`
    } else if (!maxValue.length && minValue.length) {
      this.valueContainer.innerHTML = `> ${this.valueContainerFormat.to(
        +minValue
      )}`
    } else {
      this.valueContainer.innerHTML = ''
    }
  }

  resetListInstances() {
    this.minListInstance.search()
    this.minListInstance.filter()
    this.maxListInstance.search()
    this.maxListInstance.filter()

    this.minListInstance.filter(
      item => item.values().period === this.selectedPeriod
    )
    this.maxListInstance.filter(
      item => item.values().period === this.selectedPeriod
    )
  }

  updateListContainer(clearInputs = false) {
    this.resetListInstances()

    if (clearInputs) {
      ;[this.minInput, this.maxInput].forEach(input => {
        input.value = ''
      })
    }
  }

  updateListSearch(input) {
    const { listMinType, listMaxType } = this.options
    const { valueInput } = input.dataset
    const { value } = input

    if (valueInput === listMinType) {
      this.minListInstance.search(value)
      this.maxListInstance.filter(
        item =>
          item.values().period === this.selectedPeriod &&
          +item.values().value > +value
      )
    } else if (valueInput === listMaxType) {
      this.minListInstance.filter(item => {
        const currentPeriod = item.values().period === this.selectedPeriod

        if (value.length) {
          if (+value > 0 && +item.values().value < +value) {
            return currentPeriod
          }

          return false
        }

        return currentPeriod
      })
      this.maxListInstance.search(value)
    }
  }

  init() {
    const {
      listOptionsSelector,
      dropdownOpenClass,
      listMinType,
      listMaxType,
    } = this.options
    const inputs = [this.minInput, this.maxInput]

    clickOutside(this.container, () => {
      if (this.container.classList.contains(dropdownOpenClass)) {
        this.container.classList.remove(dropdownOpenClass)
        this.checkCollapsed()
      }
    })

    this.containerWrapper.addEventListener('click', () => {
      this.container.classList.toggle(dropdownOpenClass)
      this.checkCollapsed()
    })

    this.periodRadioInputs.forEach(input => {
      input.addEventListener('change', () => {
        if (input.checked) {
          this.selectedPeriod = this.getSelectedPeriod()
          this.updateListContainer(true)
          this.updateValueContainer()
        }
      })
    })

    inputs.forEach(input => {
      input.addEventListener(
        'input',
        debounce(() => {
          this.updateListSearch(input)
          this.updateValueContainer()
        }, 200)
      )
      input.addEventListener('change', e => {
        const {
          value,
          dataset: { valueInput },
        } = e.target

        if (+value <= 0) {
          input.value = ''
        } else if (
          valueInput === listMaxType &&
          this.minInput.value &&
          +value <= +this.minInput.value
        ) {
          input.value = ''
        } else if (
          valueInput === listMinType &&
          this.maxInput.value &&
          +value >= +this.maxInput.value
        ) {
          input.value = ''
        }
      })
    })
    this.dropdownLists.forEach(list => {
      const listItems = list.querySelectorAll(listOptionsSelector)

      listItems.forEach(item => {
        const { value } = item.dataset

        item.innerText = this.optionsFormat.to(+value)
      })

      list.addEventListener('click', e => {
        e.stopPropagation()

        const { type } = list.dataset
        const { value } = e.target.dataset

        if (
          e.target.tagName.toLocaleLowerCase() ===
          listOptionsSelector.toLocaleLowerCase()
        ) {
          if (type === listMinType) {
            this.minInput.value = value
            this.minInput.dispatchEvent(createEvent('change'))
          } else if (type === listMaxType) {
            this.maxInput.value = value
            this.maxInput.dispatchEvent(createEvent('change'))
          }
        }

        inputs.forEach(input => {
          this.updateListSearch(input)
        })
        this.updateValueContainer()
      })
    })

    inputs.forEach(input => {
      this.updateListSearch(input)
    })

    this.updateListContainer()
    this.updateValueContainer()
    this.checkCollapsed()
  }
}

export default CurrencyList
